import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { CabecalhoIdentificacao } from '@/components/apreciacao/cabecalho-identificacao';
import { Etapa1DadosMaquina } from '@/components/apreciacao/etapa1-dados-maquina';
import { TabelaRiscos } from '@/components/apreciacao/tabela-riscos';
import { LockIndicator } from '@/components/lock/LockIndicator';
import { apiService } from '@/services/api';
import { useLock } from '@/hooks/useLock';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { USUARIO_PADRAO_LAUAN, USUARIO_PADRAO_LUCIANO } from '@/constants/usuarios-padrao';

export function ApreciacaoCreate() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { projeto } = useProjeto();
  const [loading, setLoading] = useState(false);
  const [apreciacaoId, setApreciacaoId] = useState<string | undefined>(undefined);
  const hoje = new Date().toISOString().split('T')[0];
  // Textos-semente (título, histórico de acidentes, limites) são conteúdo do
  // laudo: vão para o banco e saem no PDF em português. Não são rótulo de tela
  // e por isso não passam pelo dicionário.
  const [dados, setDados] = useState({
    numero: '',
    numeroArcelorMittal: '',
    revisao: 0,
    numeroEmitente: '',
    emitente: 'ACE Schmersal',
    emitirCREA: '',
    codigoInterno: '',
    titulo: 'NR-12 – Apreciação de Riscos',
    maquinaId: '',
    ativoId: '',
    ativo: undefined as any,
    maquina: {
      nome: '',
      modelo: '',
      fabricante: '',
      site: 'Arcelor Mittal',
      area: '',
      local: '',
      serie: '',
      ano: undefined as number | undefined,
      descricao: '',
    },
    dataEmissao: hoje,
    dataElaboracao: hoje,
    dataVerificacao: hoje,
    dataAprovacao: hoje,
    statusLiberacao: 'nao_liberado',
    operadorEntrevistado: '',
    cargoOperador: '',
    experiencia: 0,
    turnosTrabalho: '',
    frequenciaUso: '',
    modoOperacao: '',
    historicoAcidentes: 'A máquina não possui histórico de acidentes',
    limitesUso: 'A máquina é operada por comando local e o operador precisa realizar manualmente a inserção e retirada as peças. \nMau uso razoavelmente previsível conforme previsto na ABNT NBR ISO 12100:\na)\tNão remover ou desativar dispositivos de segurança;\nb)\tNão operar a máquina acima da capacidade prevista;\nc)\tNão utilizar a máquina para funções diferentes das previstas no projeto;\nd)\tNão realizar a manutenção sem o treinamento adequado;\ne)\tNão operar a máquina sem o treinamento adequado;\nf)\tNão ignorar sinais de falha ou alertas da máquina.',
    limitesEspaco: 'O leiaute permite o livre acesso a todos os pontos de operação e manutenção da máquina, conforme atividades a serem realizadas, mesmo com as proteções abertas. Pessoas em treinamento somente poderão operar a máquina com supervisão permanente de uma pessoa capacitada, e autorizada (mínimo), sendo a responsabilidade do treinamento somente por um profissional legalmente habilitado.',
    limitesAdicionais: 'A máquina segue os procedimento de troca de peças por desgaste conforme manutenções preventivas e corretivas.',
    limitesAmbientais: '',
    limitacoes: '',
    mauUsoRazoavelmentePrevisivel: [] as string[],
    normasTecnicasReferencia: [] as string[],
    nota1: '',
    nota2: '',
    observacoesEntrevista: '',
    dataEntrevista: hoje,
    espacoPecasFerramentas: undefined,
    espacoOperacaoNormal: undefined,
    espacoManutencao: undefined,
    espacoAjuste: undefined,
    espacoLimpeza: undefined,
    fotoPanoramicaUrl: undefined,
    fotoIdentificacaoUrl: undefined,
    nomeMaquina: '',
    tipoMaquina: '',
    identificacaoMaquina: '',
    setorMaquina: '',
    // Campos de usuários (executante/elaborador e responsável técnico)
    executanteUserId: undefined as string | undefined,
    elaboradorUserId: undefined as string | undefined,
    executanteExterno: { ...USUARIO_PADRAO_LAUAN } as any,
    elaboradorExterno: { ...USUARIO_PADRAO_LAUAN } as any,
    responsavelTecnicoUserId: undefined as string | undefined,
    responsavelTecnicoExterno: { ...USUARIO_PADRAO_LUCIANO } as any,
    verificadorUserId: undefined as string | undefined,
    verificadorExterno: { ...USUARIO_PADRAO_LAUAN } as any,
    aprovadorUserId: undefined as string | undefined,
    aprovadorExterno: { ...USUARIO_PADRAO_LUCIANO } as any,
    // Lista de verificação administrativa
    listaVerificacaoAdministrativa: undefined as any,
    // Campos técnicos da máquina
    frequenciaAcessoOperacao: '',
    frequenciaAcessoManutencao: '',
    comprimentoHorizontal: '',
    elevacao: '',
    capacidadeCorreia: '',
    larguraCorreia: '',
    velocidadeCorreia: '',
    material: '',
    contraRecuo: '',
    freio: '',
    potencia: '',
    tensaoAcionamento: '',
    tensaoComando: '',
    funcaoMaquina: '',
    numeroDocumento: '',
    demanda: '',
  });
  const [riscos, setRiscos] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [templateSelecionado, setTemplateSelecionado] = useState<string>('');
  const [carregandoTemplate, setCarregandoTemplate] = useState(false);

  // Hook para gerenciar lock do ativo/máquina selecionado
  const lockAtivo = useLock('ativo', dados.ativoId || null, {
    autoLock: !!dados.ativoId,
    autoRenovar: true,
  });

  useEffect(() => {
    if (projeto) {
      carregarTemplates();
    }
  }, [projeto]);

  // Quando ativoId mudar, verificar/criar lock
  useEffect(() => {
    if (dados.ativoId) {
      lockAtivo.verificar().then((info) => {
        if (info && !info.bloqueado) {
          lockAtivo.criar();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados.ativoId]);

  const carregarTemplates = async () => {
    if (!projeto) return;
    
    try {
      const response = await apiService.listarTemplates({ projetoId: projeto.id, ativo: true, limit: 100 });
      if (response.success && response.data) {
        setTemplates(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const handleSelecionarTemplate = async (templateId: string) => {
    if (!templateId) {
      setTemplateSelecionado('');
      return;
    }

    setCarregandoTemplate(true);
    try {
      const response = await apiService.buscarTemplate(templateId);
      if (response.success && response.data) {
        const template = response.data;

        // Aplicar configuração do template
        if (template.configuracao) {
          const config = template.configuracao;
          setDados(prev => ({
            ...prev,
            limitesUso: config.limitesUso || prev.limitesUso,
            limitesEspaco: config.limitesEspaco || prev.limitesEspaco,
            limitesAdicionais: config.limitesAdicionais || prev.limitesAdicionais,
            limitesAmbientais: config.limitesAmbientais || prev.limitesAmbientais,
            limitacoes: config.limitacoes || prev.limitacoes,
            mauUsoRazoavelmentePrevisivel: config.mauUsoRazoavelmentePrevisivel || prev.mauUsoRazoavelmentePrevisivel,
            normasTecnicasReferencia: config.normasTecnicasReferencia || prev.normasTecnicasReferencia,
            nota1: config.nota1 || prev.nota1,
            nota2: config.nota2 || prev.nota2,
          }));
        }

        // Aplicar riscos padrão do template
        if (template.riscosPadrao && Array.isArray(template.riscosPadrao)) {
          const riscosTemplate = template.riscosPadrao.map((risco: any, index: number) => ({
            numero: index + 1,
            parte: risco.parte || '',
            tipoPerigo: risco.tipoPerigo || 'mecanico',
            categoria: risco.categoria || '',
            gravidadeAtual: 1,
            frequenciaAtual: 1,
            exposicaoAtual: 1,
            probabilidadeAtual: 1,
            hrnAtual: 1,
            cicloVida: risco.cicloVida || '',
            eventoPerigoso: risco.eventoPerigoso || '',
            consequencias: risco.consequencias || '',
            adequacoesTecnicas: risco.adequacoesTecnicas || '',
            medidasControle: risco.medidasControle || '',
            exigenciasNormativas: risco.exigenciasNormativas || '',
            adequacoesNormativas: risco.adequacoesNormativas || '',
            plr: risco.plr || '',
            sil: risco.sil || '',
            categoriaSeguranca: risco.categoriaSeguranca || '',
            conformeAtual: false,
          }));
          setRiscos(riscosTemplate);
        }

        setTemplateSelecionado(templateId);
      }
    } catch (error) {
      console.error('Erro ao carregar template:', error);
      alert(t('apreciacoes.criar.erroCarregarTemplate'));
    } finally {
      setCarregandoTemplate(false);
    }
  };

  const handleChange = (campo: string, valor: any) => {
    if (campo === 'maquina') {
      setDados((prev) => ({
        ...prev,
        maquina: valor,
      }));
    } else if (campo === 'ativo') {
      setDados((prev) => ({
        ...prev,
        ativoId: valor?.id || '',
        ativo: valor,
        identificacaoMaquina: valor?.tag || prev.identificacaoMaquina,
        maquina: {
          ...prev.maquina,
          area: valor?.area || prev.maquina.area,
        },
      }));
    } else {
      setDados((prev) => ({
        ...prev,
        [campo]: valor,
      }));
    }
  };

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault();

    // Verificar se o ativo está bloqueado por outro usuário
    if (dados.ativoId && lockAtivo.bloqueadoPorOutro()) {
      alert(t('apreciacoes.criar.ativoBloqueado'));
      return;
    }

    setLoading(true);

    try {
      // Primeiro, criar a máquina se necessário (simplificado - em produção, teria um endpoint separado)
      // Por enquanto, vamos assumir que a máquina já existe ou será criada separadamente

      const dadosApreciacao = {
        numero: dados.numero || '', // Deixar vazio para o backend gerar automaticamente
        titulo: dados.titulo,
        descricao: dados.titulo,
        maquinaId: dados.maquinaId || 'temp', // Em produção, isso viria de um select de máquinas
        ativoId: dados.ativoId || undefined,
        templateId: templateSelecionado || undefined,
        emitente: dados.emitente,
        numeroEmitente: dados.numeroEmitente,
        emitirCREA: dados.emitirCREA,
        numeroArcelorMittal: dados.numeroArcelorMittal,
        codigoInterno: dados.codigoInterno,
        statusLiberacao: dados.statusLiberacao,
        dataEmissao: dados.dataEmissao,
        // Campos do cabeçalho/identificação documental
        revisao: dados.revisao,
        numeroDocumento: dados.numeroDocumento,
        nomeMaquina: dados.nomeMaquina,
        tipoMaquina: dados.tipoMaquina,
        identificacaoMaquina: dados.identificacaoMaquina,
        setorMaquina: dados.setorMaquina,
        // Limites
        limitesUso: dados.limitesUso,
        limitesEspaco: dados.limitesEspaco,
        limitesAdicionais: dados.limitesAdicionais,
        limitesAmbientais: dados.limitesAmbientais,
        limitacoes: dados.limitacoes,
        // Mau uso e normas
        mauUsoRazoavelmentePrevisivel: dados.mauUsoRazoavelmentePrevisivel,
        normasTecnicasReferencia: dados.normasTecnicasReferencia,
        nota1: dados.nota1,
        nota2: dados.nota2,
        // Entrevistados e dados de uso
        operadorEntrevistado: dados.operadorEntrevistado,
        cargoOperador: dados.cargoOperador,
        experiencia: dados.experiencia,
        turnosTrabalho: dados.turnosTrabalho,
        frequenciaUso: dados.frequenciaUso,
        frequenciaAcessoOperacao: dados.frequenciaAcessoOperacao,
        frequenciaAcessoManutencao: dados.frequenciaAcessoManutencao,
        modoOperacao: dados.modoOperacao,
        historicoAcidentes: dados.historicoAcidentes,
        observacoesEntrevista: dados.observacoesEntrevista,
        dataEntrevista: dados.dataEntrevista,
        // Informações técnicas da máquina
        comprimentoHorizontal: dados.comprimentoHorizontal,
        elevacao: dados.elevacao,
        capacidadeCorreia: dados.capacidadeCorreia,
        larguraCorreia: dados.larguraCorreia,
        velocidadeCorreia: dados.velocidadeCorreia,
        material: dados.material,
        contraRecuo: dados.contraRecuo,
        freio: dados.freio,
        potencia: dados.potencia,
        tensaoAcionamento: dados.tensaoAcionamento,
        tensaoComando: dados.tensaoComando,
        // Espaços disponíveis
        espacoPecasFerramentas: dados.espacoPecasFerramentas,
        espacoOperacaoNormal: dados.espacoOperacaoNormal,
        espacoManutencao: dados.espacoManutencao,
        espacoAjuste: dados.espacoAjuste,
        espacoLimpeza: dados.espacoLimpeza,
        // Imagens
        fotoPanoramicaUrl: dados.fotoPanoramicaUrl,
        fotoIdentificacaoUrl: dados.fotoIdentificacaoUrl,
        // Função da máquina
        funcaoMaquina: dados.funcaoMaquina,
        demanda: dados.demanda,
      };

      if (!projeto) {
        alert(t('apreciacoes.criar.selecioneProjeto'));
        return;
      }

      const response = await apiService.criarApreciacao({
        ...dadosApreciacao,
        projetoId: projeto.id,
        // Campos adicionais do Etapa1DadosMaquina
        executanteUserId: dados.executanteUserId,
        elaboradorUserId: dados.elaboradorUserId,
        executanteExterno: dados.executanteExterno,
        elaboradorExterno: dados.elaboradorExterno,
        responsavelTecnicoUserId: dados.responsavelTecnicoUserId,
        responsavelTecnicoExterno: dados.responsavelTecnicoExterno,
        verificadorUserId: dados.verificadorUserId,
        verificadorExterno: dados.verificadorExterno,
        aprovadorUserId: dados.aprovadorUserId,
        aprovadorExterno: dados.aprovadorExterno,
        listaVerificacaoAdministrativa: dados.listaVerificacaoAdministrativa,
      });

      if (response.success && response.data) {
        const novaApreciacaoId = response.data.id;
        
        // Atualizar apreciacaoId para permitir upload de fotos se necessário
        setApreciacaoId(novaApreciacaoId);

        // Atualizar dados da máquina imediatamente após criação
        // (o backend não processa o objeto maquina na criação, apenas na atualização)
        if (dados.maquina && (dados.maquina.site || dados.maquina.area || dados.maquina.local || 
            dados.maquina.fabricante || dados.maquina.modelo || dados.maquina.nome ||
            dados.maquina.descricao ||
            dados.maquina.serie || dados.maquina.ano)) {
          try {
            await apiService.atualizarApreciacao(novaApreciacaoId, {
              maquina: {
                site: dados.maquina.site || null,
                area: dados.maquina.area || null,
                local: dados.maquina.local || null,
                fabricante: dados.maquina.fabricante || null,
                modelo: dados.maquina.modelo || null,
                nome: dados.maquina.nome || null,
                descricao: dados.maquina.descricao || null,
                serie: dados.maquina.serie || null,
                ano: dados.maquina.ano || null,
              },
            });
          } catch (error) {
            console.warn('Erro ao atualizar dados da máquina após criação:', error);
            // Não bloquear o fluxo se a atualização da máquina falhar
          }
        }

        // Sem template: persistir riscos manuais da tela de criação.
        // Com template selecionado, o backend já aplica os riscos com cópia fiel.
        if (riscos.length > 0 && !templateSelecionado) {
          for (const risco of riscos) {
            try {
              await apiService.adicionarRisco(novaApreciacaoId, risco);
            } catch (error) {
              console.error('Erro ao adicionar risco:', error);
              // Continua adicionando os outros riscos mesmo se um falhar
            }
          }
        }

        navigate(`/apreciacoes/${novaApreciacaoId}`);
      } else {
        const errorMessage = response.errors?.length
          ? response.errors.join('\n')
          : response.message || t('apreciacoes.criar.erroCriar');

        console.error('Erro ao criar apreciação:', response);
        console.error('Dados enviados:', dadosApreciacao);

        alert(t('apreciacoes.criar.erroCriarDetalhe', { detalhe: errorMessage }));
      }
    } catch (error) {
      console.error('Erro ao salvar apreciação:', error);
      alert(t('apreciacoes.criar.erroSalvar'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdicionarRisco = () => {
    const novoNumero = riscos.length + 1;
    setRiscos([
      ...riscos,
      {
        numero: novoNumero,
        parte: '',
        tipoPerigo: 'mecanico',
        categoria: '',
        gravidadeAtual: 1,
        frequenciaAtual: 1,
        exposicaoAtual: 1,
        probabilidadeAtual: 1,
        hrnAtual: 1,
        cicloVida: '',
        eventoPerigoso: '',
        consequencias: '',
        conformeAtual: false,
      },
    ]);
  };

  const handleEditarRisco = (risco: any) => {
    // Em produção, abriria um modal ou navegaria para página de edição
    console.log('Editar risco:', risco);
  };

  const handleRemoverRisco = (riscoId: string) => {
    setRiscos(riscos.filter((r) => r.id !== riscoId));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0">
        <div className="flex items-center">
          <Link
            to="/apreciacoes"
            className="mr-4 p-2.5 rounded-lg text-schmersal-gray-medium 
                     hover:text-schmersal-blue hover:bg-schmersal-gray-light 
                     transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue dark:text-white">{t('apreciacoes.criar.titulo')}</h1>
            <p className="mt-1 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
              {t('apreciacoes.criar.subtitulo')}
            </p>
          </div>
        </div>
      </div>

      {/* Lock Indicator para ativo selecionado */}
      {dados.ativoId && lockAtivo.lockInfo && (
        <LockIndicator
          lockInfo={lockAtivo.lockInfo}
          eMeuLock={lockAtivo.eMeuLock()}
          bloqueadoPorOutro={lockAtivo.bloqueadoPorOutro()}
          className="mb-4"
        />
      )}

      {/* Aviso se ativo bloqueado por outro usuário */}
      {dados.ativoId && lockAtivo.bloqueadoPorOutro() && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">
            <strong>{t('apreciacoes.aviso.atencao')}</strong> {t('apreciacoes.criar.avisoAtivoEmUso')}
          </p>
        </div>
      )}

      <form onSubmit={handleSalvar} className="space-y-6">
        {/* Seleção de Template */}
        <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
          <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-3">
            {t('apreciacoes.criar.usarTemplate')}
          </label>
          <select
            value={templateSelecionado}
            onChange={(e) => handleSelecionarTemplate(e.target.value)}
            disabled={carregandoTemplate}
            className="block w-full pl-3 pr-10 py-2.5 text-base border border-rg-line dark:border-schmersal-gray-dark 
                     rounded-lg focus:outline-none focus:ring-2 focus:ring-schmersal-blue 
                     focus:border-schmersal-blue bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                     sm:text-sm transition-all duration-200"
          >
            <option value="">{t('apreciacoes.criar.selecioneTemplate')}</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.nome} ({template.tipo})
              </option>
            ))}
          </select>
          {carregandoTemplate && (
            <p className="mt-3 text-sm text-schmersal-gray-medium dark:text-white font-medium">{t('apreciacoes.criar.carregandoTemplate')}</p>
          )}
          {templateSelecionado && !carregandoTemplate && (
            <p className="mt-3 text-sm text-green-600 dark:text-green-400 font-semibold">{t('apreciacoes.criar.templateSelecionado')}</p>
          )}
        </div>

        {/* 1. Cabeçalho / Identificação Documental */}
        <CabecalhoIdentificacao
          dados={{
            ...dados,
            maquina: dados.maquina?.nome || '',
          }}
          onChange={handleChange}
          modoEdicao={true}
        />

        {/* 2. Dados da Máquina (Etapa 1) */}
        <Etapa1DadosMaquina
          dados={dados}
          onChange={handleChange}
          modoEdicao={true}
          apreciacaoId={apreciacaoId}
        />

        {/* 3. Tabela Geral das Partes */}
        <TabelaRiscos
          riscos={riscos}
          onAdicionar={handleAdicionarRisco}
          onEditar={handleEditarRisco}
          onRemover={handleRemoverRisco}
          modoEdicao={true}
        />

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-3 pt-6 border-t-2 border-schmersal-gray-light bg-white p-6 rounded-lg shadow-sm">
          <Link
            to="/apreciacoes"
            className="w-full sm:w-auto text-center bg-white py-2.5 px-5 border border-rg-line rounded-lg 
                     shadow-md text-sm font-semibold text-schmersal-gray-dark 
                     hover:bg-schmersal-gray-light focus:outline-none focus:ring-2 
                     focus:ring-offset-2 focus:ring-schmersal-blue transition-all duration-200"
          >
            {t('comum.acao.cancelar')}
          </Link>
          <button
            type="button"
            onClick={handleSalvar}
            disabled={loading}
            className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border border-schmersal-blue rounded-lg 
                     shadow-md text-sm font-semibold text-schmersal-blue bg-white 
                     hover:bg-schmersal-blue hover:text-white focus:outline-none focus:ring-2 
                     focus:ring-offset-2 focus:ring-schmersal-blue disabled:opacity-50 
                     transition-all duration-200"
          >
            <Save className="h-5 w-5 mr-2" />
            {loading ? t('comum.acao.salvando') : t('apreciacoes.criar.salvarRascunho')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border-2 border-transparent rounded-lg 
                     shadow-md text-sm font-semibold text-white bg-schmersal-blue 
                     hover:bg-schmersal-blue-dark focus:outline-none focus:ring-2 
                     focus:ring-offset-2 focus:ring-schmersal-blue disabled:opacity-50 
                     transition-all duration-200"
          >
            {t('apreciacoes.criar.criarApreciacao')}
          </button>
        </div>
      </form>
    </div>
  );
}
